import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, interval, Subject, Subscription } from 'rxjs';
import { switchMap, debounceTime } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { FatTemplateService } from '../../core/services/fat-template.service';
import { FatTemplate, FatTemplateCategory, FatTemplateSubcategory, FatTemplateField, FatTemplateStatus } from '../../core/models/models';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog.component';
import { TaskNotificationService } from '../../core/services/task-notification.service';

import { CreateTemplateDialogComponent } from './create-template-dialog.component';

@Component({
  selector: 'app-template-fat',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, ConfirmDialogComponent, CreateTemplateDialogComponent, MatPaginatorModule, MatTabsModule],
  templateUrl: './template-fat.component.html',
  styleUrls: ['./template-fat.component.css']
})
export class TemplateFatComponent implements OnInit, OnDestroy {
  private fatTemplateService = inject(FatTemplateService);
  private cdr = inject(ChangeDetectorRef);
  private taskNotificationService = inject(TaskNotificationService);

  template: FatTemplate | null = null;
  isSidebarCollapsed = signal(false);

  availableVersions: string[] = [];
  selectedVersion: string = '';
  get isLatest(): boolean { return !!this.availableVersions.length && this.selectedVersion === this.availableVersions[0]; }
  get isValidated(): boolean { return this.template?.validated === true || this.template?.status === FatTemplateStatus.VALIDATED; }
  get isDraft(): boolean { return this.template?.status === FatTemplateStatus.DRAFT || (!this.template?.validated && !this.template?.status); }
  get canEdit(): boolean { return this.isLatest && !this.isValidated; }
  get canCreateNewVersion(): boolean { return !this.template || this.isValidated; }
  get templateStatus(): string {
    if (!this.template) return '';
    if (this.isValidated) return 'Validé';
    return 'Brouillon';
  }

  loading = true;
  saving = false;
  successMessage = '';
  errorMessage = '';

  // Auto-save
  private autoSaveSubject = new Subject<void>();
  private autoSaveSub?: Subscription;

  showCreateDialog = false;
  showConfirmValidate = false;
  showConfirmDelete = false;
  deleteCallback: (() => void) | null = null;
  deleteMessage = '';
  private importPollingSub?: Subscription;
  private importJobId?: number;
  private importTaskNotificationId?: string;

  activeCategoryIndex = 0;
  fieldPageSize = 25;
  subcategoryPageIndices: { [key: string]: number } = {};

  ngOnInit() {
    this.loadInitialData();
    this.setupAutoSave();
  }

  ngOnDestroy() {
    this.stopImportPolling();
    this.stopAutoSave();
  }

  private setupAutoSave() {
    this.autoSaveSub = this.autoSaveSubject.pipe(
      debounceTime(1000) // Attendre 500ms après la dernière modification
    ).subscribe(() => {
      this.performAutoSave();
    });
  }

  private stopAutoSave() {
    if (this.autoSaveSub) {
      this.autoSaveSub.unsubscribe();
      this.autoSaveSub = undefined;
    }
  }

  triggerAutoSave() {
    if (this.canEdit && this.template) {
      this.autoSaveSubject.next();
    }
  }

  private performAutoSave() {
    if (!this.template || !this.selectedVersion || !this.canEdit) return;

    this.saving = true;
    this.errorMessage = '';

    this.fatTemplateService.updateTemplate(this.selectedVersion, this.template).subscribe({
      next: (data) => {
        this.template = data;
        this.saving = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.errorMessage = err.error?.message || 'Erreur lors de la sauvegarde automatique';
        this.saving = false;
        this.cdr.detectChanges();
      }
    });
  }

  private loadInitialData() {
    this.loading = true;
    this.errorMessage = '';

    console.log('[DEBUG_LOG] TemplateFatComponent: loadInitialData start');
    // On utilise forkJoin pour charger les versions et le template le plus récent en même temps
    // Cela garantit que toutes les données sont prêtes avant d'afficher le contenu
    forkJoin({
      versions: this.fatTemplateService.getVersions(),
      latest: this.fatTemplateService.getLatestTemplate()
    }).subscribe({
      next: ({ versions, latest }) => {
        console.log('[DEBUG_LOG] TemplateFatComponent: loadInitialData success', { versions, latest });
        this.availableVersions = versions || [];

        if (this.availableVersions.length > 0) {
          // On s'assure que latest.version est bien dans availableVersions
          // Si latest est V0 mais que versions contient [V1, V0], on prend V1
          this.selectedVersion = this.availableVersions[0];
          if (latest && latest.version === this.selectedVersion) {
            this.template = latest;
            this.loading = false;
            this.cdr.detectChanges();
          } else {
            this.loadTemplateByVersion(this.selectedVersion);
          }
        } else {
          console.log('[DEBUG_LOG] TemplateFatComponent: no versions found');
          this.template = null;
          this.selectedVersion = '';
          this.availableVersions = [];
          this.loading = false;
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        console.error('[DEBUG_LOG] TemplateFatComponent: loadInitialData error', err);
        this.errorMessage = 'Erreur lors du chargement des données initiales';
        this.loading = false;
        this.template = null;
        this.selectedVersion = '';
        this.availableVersions = [];
        this.cdr.detectChanges();
      }
    });
  }

  private refreshVersions() {
    this.fatTemplateService.getVersions().subscribe({
      next: (versions) => {
        if (this.template) {
          this.availableVersions = versions && versions.length ? versions : [this.template.version];
        } else {
          this.availableVersions = versions || [];
        }
        this.cdr.detectChanges();
      }
    });
  }

  toggleSidebar() {
    this.isSidebarCollapsed.update(v => !v);
  }

  onVersionChange(version: string) {
    if (!version || version === this.selectedVersion) { return; }
    this.selectedVersion = version;
    this.loadTemplateByVersion(version);
  }

  private loadTemplateByVersion(version: string) {
    this.loading = true;
    this.errorMessage = '';
    this.activeCategoryIndex = 0;
    this.subcategoryPageIndices = {};
    console.log('[DEBUG_LOG] TemplateFatComponent: loadTemplateByVersion start', version);
    this.fatTemplateService.getByVersion(version).subscribe({
      next: (data) => {
        console.log('[DEBUG_LOG] TemplateFatComponent: getByVersion success', data.version);
        this.template = data;
        // Assure la cohérence de la sélection si elle est vide
        if (!this.selectedVersion) {
          this.selectedVersion = data.version;
        }
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('[DEBUG_LOG] TemplateFatComponent: getByVersion error', err);
        this.errorMessage = `Erreur lors du chargement de la version ${version}`;
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  addCategory() {
    if (!this.template) return;
    this.template.categories.push({
      name: 'Nouvelle Catégorie',
      subcategories: []
    });
    this.triggerAutoSave();
  }

  removeCategory(index: number) {
    if (!this.template) return;
    this.deleteMessage = `Êtes-vous sûr de vouloir supprimer la catégorie "${this.template.categories[index].name}" et toutes ses sous-catégories ?`;
    this.deleteCallback = () => {
      this.template?.categories.splice(index, 1);
      if (this.activeCategoryIndex >= (this.template?.categories.length || 0)) {
        this.activeCategoryIndex = Math.max(0, (this.template?.categories.length || 0) - 1);
      }
      this.triggerAutoSave();
    };
    this.showConfirmDelete = true;
  }

  addSubcategory(category: FatTemplateCategory) {
    category.subcategories.push({
      name: 'Nouvelle Sous-catégorie',
      fields: []
    });
    this.triggerAutoSave();
  }

  removeSubcategory(category: FatTemplateCategory, index: number) {
    this.deleteMessage = `Êtes-vous sûr de vouloir supprimer la sous-catégorie "${category.subcategories[index].name}" ?`;
    this.deleteCallback = () => {
      category.subcategories.splice(index, 1);
      this.triggerAutoSave();
    };
    this.showConfirmDelete = true;
  }

  addField(subcategory: FatTemplateSubcategory) {
    const nextOrderId = subcategory.fields.length > 0
      ? Math.max(...subcategory.fields.map(f => f.orderId)) + 1
      : 1;

    subcategory.fields.push({
      orderId: nextOrderId,
      name: 'Nouveau Champ',
      result: '',
      comment: '',
      possibleResults: 'Conforme,Non Conforme,Non Applicable',
      commentMandatory: false,
      type: 'ACTION',
      minPhotosNbr: 1,
      comptage: '',
      transformerType: '',
      includeInReportAnnexes: false
    });
    this.triggerAutoSave();
  }

  onFieldTypeChange(field: FatTemplateField) {
    if (field.type !== 'DOCUMENT') {
      field.includeInReportAnnexes = false;
    }
    this.triggerAutoSave();
  }

  isResultSelected(field: FatTemplateField, option: string): boolean {
    if (!field.possibleResults) return false;
    return field.possibleResults.split(',').includes(option);
  }

  toggleResultOption(field: FatTemplateField, option: string) {
    let results = field.possibleResults ? field.possibleResults.split(',') : [];
    if (results.includes(option)) {
      results = results.filter((r: string) => r !== option);
    } else {
      results.push(option);
    }
    field.possibleResults = results.join(',');
    this.triggerAutoSave();
  }

  removeField(subcategory: FatTemplateSubcategory, index: number) {
    this.deleteMessage = `Êtes-vous sûr de vouloir supprimer le champ "${subcategory.fields[index].name}" ?`;
    this.deleteCallback = () => {
      subcategory.fields.splice(index, 1);
      // Reset to previous page if current page becomes empty
      const pageIndex = this.subcategoryPageIndices[subcategory.name] || 0;
      if (pageIndex > 0 && pageIndex * this.fieldPageSize >= subcategory.fields.length) {
        this.subcategoryPageIndices[subcategory.name] = pageIndex - 1;
      }
      this.triggerAutoSave();
    };
    this.showConfirmDelete = true;
  }

  getPaginatedFields(subcategory: FatTemplateSubcategory): FatTemplateField[] {
    const pageIndex = this.subcategoryPageIndices[subcategory.name] || 0;
    const start = pageIndex * this.fieldPageSize;
    return subcategory.fields.slice(start, start + this.fieldPageSize);
  }

  onPageChange(subcategory: FatTemplateSubcategory, event: any) {
    this.subcategoryPageIndices[subcategory.name] = event.pageIndex;
  }


  onValidate() {
    if (!this.template) return;
    this.showConfirmValidate = true;
  }

  onCreateTemplate() {
    this.showCreateDialog = true;
  }

  handleCreateChoice(type: 'scratch' | 'duplicate') {
    this.showCreateDialog = false;
    this.loading = true;

    let templateToSave: FatTemplate;
    if (type === 'scratch') {
      templateToSave = { version: '', categories: [] };
    } else {
      if (!this.template) return;
      templateToSave = JSON.parse(JSON.stringify(this.template));
    }

    this.fatTemplateService.saveNewVersion(templateToSave).subscribe({
      next: (data) => {
        this.template = data;
        this.selectedVersion = data.version;
        this.successMessage = 'Nouvelle version créée avec succès : ' + data.version;

        // On rafraîchit d'abord la liste des versions
        this.fatTemplateService.getVersions().subscribe({
          next: (versions) => {
            this.availableVersions = versions && versions.length ? versions : [data.version];
            this.loading = false;
            this.cdr.detectChanges();
          },
          error: () => {
            this.refreshVersions();
            this.loading = false;
            this.cdr.detectChanges();
          }
        });

        setTimeout(() => {
          this.successMessage = '';
          this.cdr.detectChanges();
        }, 5000);
      },
      error: () => {
        this.errorMessage = 'Erreur lors de la création de la nouvelle version';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  handleImport(file: File) {
    this.showCreateDialog = false;
    this.loading = true;
    this.errorMessage = '';
    this.successMessage = 'Import en cours...';
    this.importTaskNotificationId = this.taskNotificationService.start('Import template FAT', 'Import du template en cours...');

    this.fatTemplateService.startImport(file).subscribe({
      next: (status) => {
        this.importJobId = status.jobId;
        this.startImportPolling();
      },
      error: (err) => {
        if (this.importTaskNotificationId) {
          this.taskNotificationService.fail(this.importTaskNotificationId, 'L\'import du template a échoué.');
          this.importTaskNotificationId = undefined;
        }
        this.errorMessage = err.error?.message || 'Erreur lors du lancement de l\'import .xlsx';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private startImportPolling() {
    if (!this.importJobId) {
      this.loading = false;
      return;
    }
    this.stopImportPolling();
    this.importPollingSub = interval(3000)
      .pipe(switchMap(() => this.fatTemplateService.getImportStatus(this.importJobId!)))
      .subscribe({
        next: (status) => {
          if (status.status === 'SUCCESS' && status.templateVersion) {
            this.stopImportPolling();
            this.handleImportSuccess(status.templateVersion);
          } else if (status.status === 'FAILED') {
            this.stopImportPolling();
            if (this.importTaskNotificationId) {
              this.taskNotificationService.fail(this.importTaskNotificationId, status.errorMessage || 'L\'import du template a échoué.');
              this.importTaskNotificationId = undefined;
            }
            this.errorMessage = status.errorMessage || 'Erreur lors de l\'import du fichier .xlsx';
            this.loading = false;
            this.successMessage = '';
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.stopImportPolling();
          if (this.importTaskNotificationId) {
            this.taskNotificationService.fail(this.importTaskNotificationId, 'Le suivi de l\'import a échoué.');
            this.importTaskNotificationId = undefined;
          }
          this.errorMessage = 'Erreur lors du suivi de l\'import';
          this.loading = false;
          this.successMessage = '';
          this.cdr.detectChanges();
        }
      });
  }

  private stopImportPolling() {
    if (this.importPollingSub) {
      this.importPollingSub.unsubscribe();
      this.importPollingSub = undefined;
    }
  }

  private handleImportSuccess(version: string) {
    this.loading = true;
    this.successMessage = 'Import réussi : nouvelle version ' + version;
    if (this.importTaskNotificationId) {
      this.taskNotificationService.succeed(this.importTaskNotificationId, `Template importé: version ${version}.`);
      this.importTaskNotificationId = undefined;
    }
    forkJoin({
      versions: this.fatTemplateService.getVersions(),
      template: this.fatTemplateService.getByVersion(version)
    }).subscribe({
      next: ({ versions, template }) => {
        this.template = template;
        this.selectedVersion = version;
        this.availableVersions = versions && versions.length ? versions : [version];
        this.loading = false;
        this.cdr.detectChanges();
        setTimeout(() => {
          this.successMessage = '';
          this.cdr.detectChanges();
        }, 5000);
      },
      error: () => {
        this.refreshVersions();
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }


  confirmValidate() {
    if (!this.template || !this.selectedVersion) return;
    this.showConfirmValidate = false;
    this.loading = true;
    this.successMessage = '';
    this.errorMessage = '';

    this.fatTemplateService.validateTemplate(this.selectedVersion).subscribe({
      next: (data) => {
        this.template = data;
        this.successMessage = 'Version ' + data.version + ' validée avec succès. Les modifications sont désormais bloquées.';
        this.loading = false;
        this.cdr.detectChanges();
        setTimeout(() => this.successMessage = '', 5000);
      },
      error: (err) => {
        this.errorMessage = err.error?.message || 'Erreur lors de la validation de la version';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  confirmDelete() {
    if (this.deleteCallback) {
      this.deleteCallback();
    }
    this.cancelDelete();
  }

  cancelDelete() {
    this.showConfirmDelete = false;
    this.deleteCallback = null;
    this.deleteMessage = '';
  }

  onExport() {
    if (!this.selectedVersion) return;
    this.fatTemplateService.exportTemplate(this.selectedVersion).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `fat-template-${this.selectedVersion}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.errorMessage = 'Erreur lors de l\'exportation du template';
        this.cdr.detectChanges();
      }
    });
  }
}
